import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Building, Users, Shield, CheckCircle2, ArrowRight, FileCheck, Handshake, HelpCircle, Scale } from 'lucide-react';
import { Button } from '../components/ui/button';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

const optionIcons = [Users, Building, Shield];
const whyIcons = [Handshake, FileCheck, HelpCircle];

export const SmallBusinessPage = () => {
  const { t } = useTranslation();
  const whoIsThisFor = t('smallBusiness.whoList', { returnObjects: true });
  const coverageOptions = t('smallBusiness.options', { returnObjects: true });
  const whyWorkWithUs = t('smallBusiness.why', { returnObjects: true });
  const ichraHelp = t('smallBusiness.ichraHelp', { returnObjects: true });

  useEffect(() => {
    document.title = `${t('smallBusiness.metaTitle')} · Calyvra Insurance`;
  }, [t]);

  return (
    <div className="overflow-hidden" data-testid="small-business-page">
      <section className="gradient-hero py-20 lg:py-28" data-testid="sb-hero">
        <div className="container-custom">
          <motion.div initial="initial" animate="animate" variants={fadeInUp} className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-600 rounded-full text-sm font-medium mb-6">
              <Building className="h-4 w-4" />
              {t('smallBusiness.heroBadge')}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-tight mb-6">
              {t('smallBusiness.heroTitlePre')}{' '}<span className="text-gradient">{t('smallBusiness.heroHighlight')}</span>
            </h1>
            <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">{t('smallBusiness.heroDesc')}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button className="btn-primary" data-testid="sb-hero-cta">
                  {t('smallBusiness.heroCta')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="btn-secondary" data-testid="sb-hero-agent">{t('smallBusiness.heroAgent')}</Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="section-spacing bg-white" data-testid="sb-who">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">{t('smallBusiness.whoTitle')}</h2>
              <p className="text-lg text-slate-600 mb-8">{t('smallBusiness.whoText')}</p>
              <ul className="space-y-4">
                {whoIsThisFor.map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-sky-500 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="bg-slate-50 rounded-2xl p-8">
              <Building className="h-12 w-12 text-slate-400 mb-6" />
              <h3 className="text-xl font-semibold text-slate-900 mb-4">{t('smallBusiness.approachTitle')}</h3>
              <p className="text-slate-600 mb-6">{t('smallBusiness.approachText')}</p>
              <p className="text-slate-500 text-sm">{t('smallBusiness.approachNote')}</p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="section-spacing gradient-subtle" data-testid="sb-options">
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{t('smallBusiness.optionsTitle')}</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">{t('smallBusiness.optionsSubtitle')}</p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8">
            {coverageOptions.map((option, index) => {
              const Icon = optionIcons[index] || Users;
              return (
                <motion.div key={option.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="bg-white p-8 rounded-2xl border border-slate-100 card-shadow" data-testid={`option-card-${index}`}>
                  <div className="h-12 w-12 bg-slate-100 rounded-xl flex items-center justify-center mb-6">
                    <Icon className="h-6 w-6 text-slate-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">{option.title}</h3>
                  <p className="text-slate-600 text-sm mb-6">{option.description}</p>
                  <ul className="space-y-2">
                    {option.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-sky-500 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section-spacing bg-white" data-testid="sb-why">
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{t('smallBusiness.whyTitle')}</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">{t('smallBusiness.whySubtitle')}</p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {whyWorkWithUs.map((item, index) => {
              const Icon = whyIcons[index] || Handshake;
              return (
                <motion.div key={item.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="text-center" data-testid={`why-card-${index}`}>
                  <div className="h-14 w-14 bg-sky-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Icon className="h-7 w-7 text-sky-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm">{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section-spacing gradient-subtle" data-testid="sb-ichra">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white text-slate-600 rounded-full text-sm font-medium mb-6 border border-slate-100">
                <Scale className="h-4 w-4" />
                ICHRA
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">{t('smallBusiness.ichraTitle')}</h2>
              <p className="text-slate-600 mb-4">{t('smallBusiness.ichraP1')}</p>
              <p className="text-slate-600">{t('smallBusiness.ichraP2')}</p>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="bg-white rounded-2xl p-8 border border-slate-100 card-shadow">
              <h3 className="text-xl font-semibold text-slate-900 mb-6">{t('smallBusiness.ichraHelpTitle')}</h3>
              <ul className="space-y-4 mb-8">
                {ichraHelp.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-sky-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="bg-slate-50 rounded-xl p-6">
                <h4 className="text-base font-semibold text-slate-900 mb-2">{t('smallBusiness.ichraCtaTitle')}</h4>
                <p className="text-slate-600 text-sm mb-5">{t('smallBusiness.ichraCtaText')}</p>
                <Link to="/contact">
                  <Button className="btn-primary" data-testid="sb-ichra-cta">
                    {t('smallBusiness.ichraCtaBtn')}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-900" data-testid="sb-cta">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">{t('smallBusiness.ctaTitle')}</h2>
              <p className="text-slate-300 mb-8">{t('smallBusiness.ctaText')}</p>
              <Link to="/contact">
                <Button className="bg-sky-500 hover:bg-sky-600 text-white h-12 px-8 rounded-full font-semibold" data-testid="sb-cta-btn">
                  {t('smallBusiness.ctaBtn')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-8 bg-slate-50 border-t border-slate-100" data-testid="sb-disclaimer">
        <div className="container-custom">
          <p className="text-slate-500 text-xs text-center max-w-3xl mx-auto">{t('smallBusiness.disclaimer')}</p>
        </div>
      </section>
    </div>
  );
};

export default SmallBusinessPage;
